import React from "react";
import { Camera, ChevronDown, Clock, Mail, Phone, Power } from "lucide-react";

// ===== GET USER INITIALS =====
export const getUserInitials = (name) => {
  if (!name) return "U";
  const words = name.trim().split(" ");
  if (words.length === 1) return words[0].charAt(0).toUpperCase();
  return (
    words[0].charAt(0) + words[words.length - 1].charAt(0)
  ).toUpperCase();
};

// ===== USER PROFILE (topbar trigger + dropdown) =====
// This block was duplicated verbatim in the RTL and LTR branches of every
// sidebar copy — now it's a single component rendered once.
const UserProfileDropdown = ({
  styles,
  t,
  language,
  isMobile,
  userData,
  profilePic,
  showUserProfile,
  toggleUserProfile,
  userProfileRef,
  getStatusColor,
  isUploadingProfilePic,
  profilePicInputRef,
  handleProfilePicSelect,
  triggerProfilePicInput,
  onLogoutClick,
}) => (
  <div ref={userProfileRef} style={{ position: "relative" }}>
    <div
      style={styles.userInfo}
      className="user-info"
      onClick={toggleUserProfile}
    >
      <div style={{ position: "relative" }}>
        {profilePic ? (
          <img
            src={userData.avatar}
            alt="User Avatar"
            style={styles.userAvatar}
          />
        ) : (
          <div
            style={{
              ...styles.userAvatar,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                "linear-gradient(135deg, #2e7cba, #2e7cba)",
              color: "white",
              fontSize: isMobile ? "0.875rem" : "1.125rem",
              fontWeight: "700",
              textTransform: "uppercase",
            }}
          >
            {getUserInitials(userData.name)}
          </div>
        )}
        <div
          style={{
            ...styles.userAvatarStatus,
            background: getStatusColor(userData.status),
          }}
        />
      </div>
      <div style={styles.userDetails} className="user-details">
        <div style={styles.userName}>
          {userData.name ? userData.name.split(" ")[0] : "User"}
        </div>
      </div>
      <ChevronDown
        size={isMobile ? 16 : 18}
        style={styles.userDropdownIcon}
        className="user-dropdown-icon"
      />
    </div>

    <div
      style={styles.userProfileDropdown}
      className="user-profile-dropdown"
      onClick={(e) => e.stopPropagation()}
    >
      <div style={styles.userProfileHeader}>
        <div
          style={{
            position: "relative",
            display: "inline-block",
          }}
        >
          {profilePic ? (
            <img
              src={userData.avatar}
              alt="User Avatar"
              style={styles.userProfileAvatar}
            />
          ) : (
            <div
              style={{
                ...styles.userProfileAvatar,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background:
                  "linear-gradient(135deg, #2e7cba, #2e7cba)",
                color: "white",
                fontSize: isMobile ? "1.75rem" : "2.25rem",
                fontWeight: "700",
                textTransform: "uppercase",
              }}
            >
              {getUserInitials(userData.name)}
            </div>
          )}

          {/* Upload button overlay */}
          <button
            onClick={triggerProfilePicInput}
            disabled={isUploadingProfilePic}
            style={{
              position: "absolute",
              bottom: "0",
              right: "0",
              width: isMobile ? "2rem" : "2.25rem",
              height: isMobile ? "2rem" : "2.25rem",
              borderRadius: "50%",
              background:
                "linear-gradient(135deg, #2e7cba, #2e7cba)",
              border: "3px solid white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: isUploadingProfilePic
                ? "not-allowed"
                : "pointer",
              transition: "all 0.3s ease",
              boxShadow: "0 4px 12px rgba(59, 130, 246, 0.4)",
              opacity: isUploadingProfilePic ? 0.6 : 1,
              color: "white",
              zIndex: 1,
            }}
            className="profile-upload-btn"
            title={
              isUploadingProfilePic
                ? t.uploading || "Uploading..."
                : t.uploadProfilePicture || "Upload profile picture"
            }
          >
            {isUploadingProfilePic ? (
              <Clock size={isMobile ? 14 : 16} />
            ) : (
              <Camera size={isMobile ? 14 : 16} />
            )}
          </button>

          {/* Hidden file input */}
          <input
            ref={profilePicInputRef}
            type="file"
            accept="image/*"
            onChange={handleProfilePicSelect}
            style={{ display: "none" }}
          />

          {/* Status indicator */}
          <div
            style={{
              position: "absolute",
              bottom: isMobile ? "0.5rem" : "0.5rem",
              left: isMobile ? "0.5rem" : "0.5rem",
              width: isMobile ? "1rem" : "1.125rem",
              height: isMobile ? "1rem" : "1.125rem",
              borderRadius: "50%",
              border: "3px solid white",
              background: getStatusColor(userData.status),
            }}
          />
        </div>

        <div style={styles.userProfileName}>
          {userData.name ||
            (language === "ar" ? "اسم المستخدم" : "User Name")}
        </div>
        <div style={styles.userProfileStatus}>
          <div
            style={{
              ...styles.userProfileStatusDot,
              background: getStatusColor(userData.status),
            }}
          />
          <span style={styles.userProfileStatusText}>
            {userData.status === "online"
              ? t.activeNow
              : userData.lastSeen}
          </span>
        </div>
      </div>

      <div style={styles.userProfileContent}>
        <div style={styles.userProfileSection}>
          <div style={styles.userProfileSectionTitle}>
            {t.contact || "Contact"}
          </div>
          <div style={styles.userProfileItem}>
            <Mail
              size={16}
              style={styles.userProfileItemIcon}
            />
            <span>
              {userData.email || t.noEmail || "No email"}
            </span>
          </div>
          <div style={styles.userProfileItem}>
            <Phone
              size={16}
              style={styles.userProfileItemIcon}
            />
            <span>
              {userData.phone || t.noPhone || "No phone"}
            </span>
          </div>
          <button
            style={styles.logoutBtn}
            onClick={onLogoutClick}
          >
            <Power size={isMobile ? 16 : 18} />
            <span className="logout-text">
              {t.logout || "Logout"}
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
);

export default UserProfileDropdown;
